import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { AdminFilterSidebar } from '../components'
import { Select, Input } from '../../../shared/components'
import adminCustomerService from '../../../services/adminCustomerService'
import {
  AdminCustomer,
  AdminCustomerSummary,
  PaginationResponse
} from '../../../types/api'
import { colors } from '../../../styles/design-system'
import AdminCustomersSkeleton from '../../../components/admin/customers/AdminCustomersSkeleton'
import AdminCustomerStats from '../../../components/admin/customers/AdminCustomerStats'
import AdminCustomersTable from '../../../components/admin/customers/AdminCustomersTable'
import AdminCustomerDetailsModal from '../../../components/admin/customers/AdminCustomerDetailsModal'
import AdminCustomersActionBar from '../../../components/admin/customers/AdminCustomersActionBar'
import { AdminCustomerSortField, AdminCustomerStatusFilter } from '../../../types/adminCustomers'

const CSS_HANDLES = [
  'adminCustomers',
  'customersHeader',
  'customersTitle',
  'customersSubtitle',
  'customersStats',
  'customersStatCard',
  'customersTable',
  'tableHeader',
  'tableRow',
  'customerName',
  'customerContact',
  'customerMetrics',
  'customerStatus',
  'customerActions',
  'statusBadge',
  'statusActive',
  'statusInactive',
  'emptyState',
  'errorState'
] as const

const ITEMS_PER_PAGE = 10

const DEFAULT_FILTERS: {
  search: string
  status: AdminCustomerStatusFilter
} = {
  search: '',
  status: 'all'
}

const DEFAULT_SUMMARY: AdminCustomerSummary = {
  totalCustomers: 0,
  activeCustomers: 0,
  totalRevenue: 0
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }

  return parsed.toLocaleDateString('pt-BR')
}

const buildSummaryFromCustomers = (
  list: AdminCustomer[]
): AdminCustomerSummary => ({
  totalCustomers: list.length,
  activeCustomers: list.filter((customer) => customer.isActive).length,
  totalRevenue: list.reduce(
    (total, customer) => total + (customer.totalSpent ?? 0),
    0
  )
})

const AdminCustomers: FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [summary, setSummary] =
    useState<AdminCustomerSummary>(DEFAULT_SUMMARY)
  const [pagination, setPagination] =
    useState<PaginationResponse | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<{
    search: string
    status: AdminCustomerStatusFilter
  }>(
    () => ({
      ...DEFAULT_FILTERS
    })
  )
  const [draftFilters, setDraftFilters] = useState<{
    search: string
    status: AdminCustomerStatusFilter
  }>(() => ({
    ...DEFAULT_FILTERS
  }))
  const [sortState, setSortState] = useState<{
    field: AdminCustomerSortField
    order: 'asc' | 'desc'
  }>({
    field: 'registrationDate',
    order: 'desc'
  })
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCustomer, setSelectedCustomer] =
    useState<AdminCustomer | null>(null)

  // Refs para evitar chamadas duplicadas
  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const previousSortStateRef = useRef<string | null>(null)
  const isLoadingRef = useRef(false)

  const fetchCustomers = useCallback(
    async (page: number) => {
      // Verificar se já está carregando para evitar chamadas simultâneas
      if (isLoadingRef.current) {
        return
      }

      // Serializar filtros e sortState para comparação
      const filtersKey = JSON.stringify({
        search: filters.search.trim(),
        status: filters.status
      })
      const sortStateKey = JSON.stringify({
        field: sortState.field,
        order: sortState.order
      })

      // Verificar se realmente mudou
      const filtersChanged = previousFiltersRef.current !== filtersKey
      const pageChanged = previousPageRef.current !== page
      const sortStateChanged = previousSortStateRef.current !== sortStateKey

      // Se nada mudou e já foi carregado, não fazer nova chamada
      if (!filtersChanged && !pageChanged && !sortStateChanged && isLoadingRef.current) {
        return
      }
      if (!filtersChanged && !pageChanged && !sortStateChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
        return
      }

      isLoadingRef.current = true
      previousFiltersRef.current = filtersKey
      previousPageRef.current = page
      previousSortStateRef.current = sortStateKey

      setIsFetching(true)
      setError(null)

      try {
        const trimmedSearch = filters.search.trim()
        const statusParam =
          filters.status === 'all' ? undefined : filters.status

        const data = await adminCustomerService.list({
          page,
          limit: ITEMS_PER_PAGE,
          search: trimmedSearch || undefined,
          status: statusParam,
          sortBy: sortState.field,
          sortOrder: sortState.order
        })

        const nextSummary = data.summary ?? buildSummaryFromCustomers(data.customers)

        setCustomers(data.customers)
        setSummary(nextSummary)
        setPagination(data.pagination)
        setHasLoaded(true)
      } catch (err) {
        console.error('Erro ao carregar clientes:', err)
        setCustomers([])
        setSummary(DEFAULT_SUMMARY)
        setPagination(null)
        setError('Não foi possível carregar os clientes. Tente novamente.')
        setHasLoaded(true)
        // Resetar refs em caso de erro para permitir nova tentativa
        previousFiltersRef.current = null
        previousPageRef.current = 1
        previousSortStateRef.current = null
      } finally {
        setIsFetching(false)
        isLoadingRef.current = false
      }
    },
    [filters, sortState.field, sortState.order]
  )

  useEffect(() => {
    fetchCustomers(currentPage)
  }, [fetchCustomers, currentPage])

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <
    Key extends keyof typeof draftFilters
  >(
    key: Key,
    value: (typeof draftFilters)[Key]
  ) => {
    setDraftFilters((previous) => ({
      ...previous,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...DEFAULT_FILTERS }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const isApplyDisabled =
    draftFilters.search === filters.search &&
    draftFilters.status === filters.status

  const handleSort = (field: AdminCustomerSortField) => {
    setSortState((previous) => {
      const nextOrder =
        previous.field === field && previous.order === 'asc'
          ? 'desc'
          : 'asc'

      return {
        field,
        order: previous.field === field ? nextOrder : 'asc'
      }
    })
    setCurrentPage(1)
  }

  const renderSortIndicator = (field: AdminCustomerSortField) => {
    if (sortState.field !== field) {
      return (
        <span
          style={{ marginLeft: 6, color: colors.neutral.gray300 }}
          aria-hidden="true"
        >
          ⇅
        </span>
      )
    }

    return (
      <span
        style={{
          marginLeft: 6,
          color: colors.primary.blue,
          display: 'inline-flex',
          alignItems: 'center'
        }}
        aria-hidden="true"
      >
        {sortState.order === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const getAriaSort = (
    field: AdminCustomerSortField
  ): 'none' | 'ascending' | 'descending' => {
    if (sortState.field !== field) {
      return 'none'
    }

    return sortState.order === 'asc' ? 'ascending' : 'descending'
  }

  const handleViewCustomer = (customer: AdminCustomer) => {
    setSelectedCustomer(customer)
  }

  const handleEditCustomer = (customer: AdminCustomer) => {
    console.warn('Editar cliente não implementado:', customer.id)
  }

  const handleToggleStatus = (customerId: string) => {
    setCustomers((previous) => {
      const updated = previous.map((customer) =>
        customer.id === customerId
          ? { ...customer, isActive: !customer.isActive }
          : customer
      )
      return updated
    })
  }

  const hasActiveFilters =
    filters.status !== 'all' || filters.search.trim() !== ''

  const activeFiltersDescriptions = useMemo(
    () =>
      [
        filters.status !== 'all'
          ? `status: ${filters.status === 'active' ? 'ativos' : 'inativos'}`
          : null,
        filters.search.trim()
          ? `busca: “${filters.search.trim()}”`
          : null
      ]
        .filter(Boolean)
        .join(' • '),
    [filters.search, filters.status]
  )

  const totalPages = pagination?.totalPages ?? 1
  const totalItems =
    pagination?.total ?? summary.totalCustomers ?? customers.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? ITEMS_PER_PAGE
  const startIndex =
    totalItems === 0 ? 0 : (currentPageNumber - 1) * limit + 1
  const endIndex =
    totalItems === 0 ? 0 : startIndex + customers.length - 1
  const showInitialSkeleton = !hasLoaded && isFetching
  const showLoadingOverlay = hasLoaded && isFetching
  const showEmptyState =
    hasLoaded && !isFetching && customers.length === 0 && !error

  if (showInitialSkeleton) {
    return (
      <AdminLayout>
        <AdminCustomersSkeleton />
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/70">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminCustomers} min-h-screen bg-gray-50`}>
          <div className="mx-auto max-w-7xl space-y-8 px-4 py-8">
            <div className={handles.customersHeader}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className={`${handles.customersTitle} text-3xl font-bold text-gray-900`}>
                    Gestão de Clientes
                  </h1>
                  <p className={`${handles.customersSubtitle} mt-2 text-lg text-gray-600`}>
                    Gerencie informações e histórico dos clientes
                  </p>
                </div>
              </div>
            </div>

            <AdminCustomerStats
              summary={summary}
              handles={handles}
              formatCurrency={formatCurrency}
              isLoading={showLoadingOverlay}
            />

            {error && !isFetching && (
              <div
                className={`${handles.errorState} rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchCustomers(currentPageNumber)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            <AdminCustomersActionBar
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={totalItems}
              hasActiveFilters={hasActiveFilters}
              activeFiltersDescriptions={activeFiltersDescriptions}
              onOpenFilters={handleOpenFilters}
            />

            <AdminCustomersTable
              customers={customers}
              handles={handles}
              showEmptyState={showEmptyState}
              renderSortIndicator={renderSortIndicator}
              getAriaSort={getAriaSort}
              onSort={handleSort}
              onView={handleViewCustomer}
              onEdit={handleEditCustomer}
              onToggleStatus={handleToggleStatus}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
            />

            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-4 rounded-xl border border-gray-100 bg-white px-6 py-4 text-sm text-gray-500 md:flex-row">
                <p>
                  Página {currentPageNumber} de {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPageNumber === 1}
                    className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPageNumber === totalPages}
                    className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <AdminFilterSidebar
          isOpen={isFilterSidebarOpen}
          onClose={handleCloseFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          isApplyDisabled={isApplyDisabled}
          applyLabel="Aplicar filtros"
          clearLabel="Limpar filtros"
        >
          <div className="space-y-6">
            <div>
              <label
                htmlFor="customer-filter-search"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Buscar clientes
              </label>
              <Input
                id="customer-filter-search"
                type="text"
                value={draftFilters.search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDraftFilterChange('search', event.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-700 shadow-sm transition-shadow focus:border-blue-500"
                placeholder="Nome, e-mail ou CPF"
              />
            </div>

            <div>
              <label
                htmlFor="customer-filter-status"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Status
              </label>
              <Select
                id="customer-filter-status"
                value={draftFilters.status}
                onChange={(value) =>
                  handleDraftFilterChange(
                    'status',
                    value as AdminCustomerStatusFilter
                  )
                }
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'active', label: 'Ativos' },
                  { value: 'inactive', label: 'Inativos' }
                ]}
                placeholder="Selecione o status"
                className="w-full"
              />
            </div>
          </div>
        </AdminFilterSidebar>

        <AdminCustomerDetailsModal
          customer={selectedCustomer}
          isOpen={Boolean(selectedCustomer)}
          onClose={() => setSelectedCustomer(null)}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      </div>
    </AdminLayout>
  )
}

export default AdminCustomers


